<script lang="ts" setup>
import type ResponseData from '@/types/ResponseData'
import CompanyDataService from '@/services/CompanyDataService'
import type Company from '@/types/Company'
import type Headquarters from '@/types/Headquarters'
import { toast } from 'vue3-toastify'
import 'vue3-toastify/dist/index.css'
import { useRouter } from "vue-router";
import { onBeforeMount, ref, computed } from 'vue';
import store from '@/store'
import AddressDataService from '@/services/AddressDataService'
import type Address from '@/types/Address'
import HeadquartersDataService from '@/services/HeadquartersDataService'

// Simple interface for order items
interface OrderItem {
    description: string;
    [key: string]: unknown;
}

const router = useRouter();

const currentOrder = computed(() => store.state.currentOrder);
const orderedAddress = ref({} as Address);
const currentCompany = ref({} as Company);
const items = ref([] as OrderItem[]);
const vhqItem = ref();
const vhqItemPackage = ref();
const priorityItem = ref();
const vhqs = ref([] as Headquarters[]);

const packages = [
    "Mini",
    "Ideal",
    "Premium",
    "Extra",
    "Individual"
];

async function getAllVhqs() {
    await HeadquartersDataService.getAllVirtualHeadquarters()
        .then((response: ResponseData) => {
            vhqs.value = response.data.data
        })
        .catch((e: Error) => {
            toast.error('Chyba: ' + e.message)
        })
}

async function getCompany(id: number) {
    await CompanyDataService.getCompanyById(id)
        .then((response: ResponseData) => {
            currentCompany.value = response.data.data
        })
        .catch((e: Error) => {
            toast.error('Chyba: ' + e.message)
        })
}

async function getCompanyAddress(id: number) {
    if (id > 0) {
        await AddressDataService.getAddressById(id)
            .then(async (response: ResponseData) => {
                orderedAddress.value = response.data.data as Address;
            })
            .catch((e: Error) => {
                toast.error('Chyba: ' + e.message)
            })
    }
}

async function refreshData() {
    await getCompany(currentOrder.value.company_id);
    items.value = JSON.parse(currentOrder.value.items);
    let num = 0;
    let isRenewalOrder = false;
    items.value.forEach((item) => {
        // Match "Služba Virtuálne sídlo: {address_id} {name} (platnosť na 1 rok)"
        if (item.description.includes("Služba Virtuálne sídlo:")) {
            // Extract the address ID right after "Virtuálne sídlo: "
            const match = item.description.match(/Služba Virtuálne sídlo:\s*(\d+)/);
            num = match ? parseInt(match[1], 10) : 0;
            // BACKWARD COMPATIBILITY: If num looks like a VHQ id instead of address id, map it
            // This handles orders created before fix (when we stored VHQ id instead of address_id)
            // Complete VHQ to address_id mapping from database
            const vhqToAddressMap: Record<number, number> = {
              1: 1,    // Ružinovská
              2: 2,    // Panorama towers
              3: 3,    // Klingerka
              4: 4,    // Miletička
              5: 5,    // SKY PARK Residence
              6: 308   // Eurovea Tower (the one that caused the bug)
            };
            if (vhqToAddressMap[num]) {
                console.log(`Backward compatibility: Converting old VHQ ID ${num} to address_id ${vhqToAddressMap[num]}`);
                num = vhqToAddressMap[num];
            }
            console.log("Address ID: " + num);
        }
        // Match "Balík služieb Virtuálneho sídla: Mini (platnosť na 1 rok)"
        if (/^Balík služieb Virtuálneho sídla[: ]/.test(item.description)) {
            vhqItem.value = item;
            // Extract the package name (e.g., "Mini", "Ideal", etc.) from the description
            const match = item.description.match(/^Balík služieb Virtuálneho sídla[: ]+([A-Za-zÁÉÍÓÚÝáéíóúýčďľňôřšťžČĎĽŇÔŘŠŤŽ]+)\b/);
            console.log("Package match: " + match);
            vhqItemPackage.value = match ? match[1] : '';
        }
        // Match renewal format: "Predlženie služby Virtuálne sídlo (Mini) pre..."
        else if (item.description.includes("Predlženie služby Virtuálne sídlo")) {
            vhqItem.value = item;
            isRenewalOrder = true;
            // Try to extract package name from description first (backend includes it in parentheses)
            const match = item.description.match(/Virtuálne sídlo\s*\(([A-Za-zÁÉÍÓÚÝáéíóúýčďľňôřšťžČĎĽŇÔŘŠŤŽ\s]+?)\)\s+pre/);
            if (match) {
                vhqItemPackage.value = match[1].trim();
                console.log("Renewal order detected, package from description: " + vhqItemPackage.value);
            } else {
                console.log("Renewal order detected, will get package from company data");
            }
        }
        // Match priority processing upsell: "Prioritné spracovanie objednávky"
        if (item.description.includes("Prioritné spracovanie objednávky")) {
            priorityItem.value = item;
        }
    });
    if (num > 0) {
        await getCompanyAddress(num);
    } else if (currentCompany.value.headquarters_id) {
        // For renewal orders, fetch address from company's headquarters
        try {
            const hqResponse: ResponseData = await HeadquartersDataService.getHeadquartersById(currentCompany.value.headquarters_id);
            const headquarters = hqResponse.data.data as Headquarters;
            if (headquarters.address_id) {
                await getCompanyAddress(headquarters.address_id);
            }
        } catch (e) {
            console.error('Error fetching headquarters address:', e);
        }
    }

    // Get package from company data if not already extracted from description (covers renewal orders and edge cases where name was not stored in order items)
    if (!vhqItemPackage.value && currentCompany.value.sidlo_typ_balika) {
        // Convert database format to display format if needed
        const packageMap: Record<string, string> = {
            'MINI': 'Mini',
            'IDEAL': 'Ideal',
            'PREMIUM': 'Premium',
            'EXTRA': 'Extra',
            'INDIVIDUAL': 'Individual'
        };
        const dbPackage = currentCompany.value.sidlo_typ_balika.toUpperCase();
        vhqItemPackage.value = packageMap[dbPackage] || currentCompany.value.sidlo_typ_balika;
    }
}

async function updateCompany() {
    currentCompany.value.sidlo_typ_balika = vhqItemPackage.value;
    await CompanyDataService.update(currentCompany.value.id, currentCompany.value)
        .catch((e: Error) => {
            toast.error('Chyba: ' + e.message);
        });

    if (currentCompany.value.headquarters_id && vhqItem.value?.price) {
        const hqResponse: ResponseData = await HeadquartersDataService.getHeadquartersById(currentCompany.value.headquarters_id);
        const hq = hqResponse.data.data as Headquarters;
        hq.price = vhqItem.value.price;
        await HeadquartersDataService.update(hq.id, hq)
            .catch((e: Error) => {
                toast.error('Chyba pri ukladaní ceny sídla: ' + e.message);
            });
    }

    router.go(0);
}

onBeforeMount(async () => {
    await getAllVhqs();
    await refreshData();
})
</script>

<template>
    <div class="max-w-2xl mx-auto bg-white rounded-2xl shadow-xl p-8 border border-gray-100 flex flex-col gap-8">
        <!-- Header -->
        <div class="flex items-center gap-4 mb-2">
            <div class="flex items-center justify-center w-12 h-12 rounded-full bg-teal-50 shadow-sm">
                <svg class="w-7 h-7 text-teal-500" fill="none" stroke="currentColor" stroke-width="2"
                    viewBox="0 0 24 24" title="Virtuálne sídlo">
                    <rect x="4" y="4" width="16" height="16" rx="3" stroke="currentColor" stroke-width="2"
                        fill="none" />
                    <path d="M8 12h8" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
                </svg>
            </div>
            <span class="text-2xl md:text-3xl font-extrabold text-slate-900 tracking-tight">Virtuálne sídlo</span>
            <span v-if="vhqItem?.price" class="ml-2 text-xl font-semibold text-teal-600" title="Cena balíka">- {{
                vhqItem?.price }}€</span>
        </div>

        <!-- Address Section -->
        <div class="flex flex-col gap-2 bg-slate-50 rounded-lg p-4 border border-slate-100">
            <label class="block text-sm font-medium text-gray-700">Adresa sídla</label>
            <select id="vhqs" name="vhqs" disabled
                class="bg-slate-100 border border-gray-200 rounded-md px-2 py-1 text-gray-700 cursor-not-allowed">
                <option v-for="vhq in vhqs" :key="vhq.address_id" :value="vhq.address_id"
                    :selected="vhq.address_id == orderedAddress.id" class="text-black">
                    {{ vhq.name }}
                </option>
            </select>
            <div class="text-sm text-slate-700 mt-1 flex items-center gap-2">
                <svg class="w-4 h-4 text-teal-400" fill="none" stroke="currentColor" stroke-width="2"
                    viewBox="0 0 24 24">
                    <path d="M17.657 16.657L13.414 20.9a2 2 0 0 1-2.828 0l-4.243-4.243a8 8 0 1 1 11.314 0z" />
                    <circle cx="12" cy="11" r="3" />
                </svg>
                {{ orderedAddress.street + ' ' + orderedAddress.street_number + (orderedAddress.street_number2 ? '/' +
                    orderedAddress.street_number2 : '') + ', ' + orderedAddress.psc + ' ' + orderedAddress.city }}
            </div>
        </div>

        <!-- Package and Payment Section -->
        <div class="flex flex-col gap-4">
            <div class="flex flex-col md:flex-row gap-4">
                <div class="flex-1 flex flex-col gap-1">
                    <label for="typ" class="block text-sm font-medium text-gray-700">Balík</label>
                    <select v-model="vhqItemPackage" name="typ" id="typ"
                        class="mt-1 focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md bg-white"
                        :class="{ 'border-red-400 ring-1 ring-red-400': !vhqItemPackage }">
                        <option value="" disabled>-- Vyberte balík --</option>
                        <option v-for="pck in packages" :key="pck" :value="pck">{{ pck }}</option>
                    </select>
                    <p v-if="!vhqItemPackage" class="mt-1 text-xs text-red-500">Balík nebol zaznamenaný v objednávke – vyberte manuálne a uložte.</p>
                </div>
                <div class="flex-1 flex flex-col gap-1">
                    <FormKit type="text" v-model="currentCompany.sidlo_zaplatene_do" name="vzaplatenedo"
                        label="Balík zaplatený do" id="vzaplatenedo"
                        class="mt-1 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md bg-white" />
                </div>
            </div>
            <div class="flex flex-row justify-end">
                <button type="button" @click="updateCompany()"
                    class="inline-flex items-center gap-2 py-2 px-5 border border-transparent shadow-sm text-sm font-semibold rounded-md text-white bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 mt-2 transition-all duration-200"
                    title="Uložiť zmeny">
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                    </svg>
                    Uložiť
                </button>
            </div>
            <p><b>Prosim <u>prever</u> či sa sídlo aktualizovalo správne.</b></p>
        </div>

        <!-- Additional Services Section -->
        <div v-if="priorityItem"
            class="flex flex-col gap-2 bg-slate-50 rounded-lg p-4 border border-slate-100">
            <label class="block text-sm font-medium text-gray-700">Doplnkové služby</label>
            <div class="flex items-center justify-between text-sm text-slate-700">
                <span class="inline-flex items-center gap-2">
                    <svg class="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" stroke-width="2"
                        viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    Prioritné spracovanie objednávky
                </span>
                <span class="font-semibold text-blue-600">+ {{ priorityItem.price }}€</span>
            </div>
        </div>
    </div>
</template>